import { useEffect, useState } from "react";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	IconButton,
	Menu,
	MenuItem,
	Typography,
} from "@mui/material";
import moment from "moment";
import { BsThreeDots } from "react-icons/bs";
import { Link } from "react-router-dom";

import { apis, callApi } from "../../../../../api";
import { usePremiumPackage } from "../../../../../hooks/usePremiumPackage";
import { currencyFormatter } from "../../../../../utils";
import { Loading } from "../../../../common/Loading/Loading";

type TMembershipAccountStatus = {
	premium: boolean;
	status: string;
	provider: "ios" | "android" | "stripe" | "manual" | null;
	manageAt: "app_store" | "play_store" | "web" | "support" | null;
};

const manageSubscriptionCopy: Record<
	NonNullable<TMembershipAccountStatus["manageAt"]>,
	string
> = {
	app_store: "Go to Apple Store to manage your subscription.",
	play_store: "Go to Play Store to manage your subscription.",
	web: "Go to flyorka.com to manage your subscription.",
	support: "Contact Fly ORKA support to manage your subscription.",
};

export function Subscriptions() {
	type TState = {
		optionButtonAnchor: HTMLElement | null;
		subscriptionId: string | null;
		confirmDialogOpen: boolean;
	};
	const [state, setState] = useState<TState>({
		optionButtonAnchor: null,
		subscriptionId: null,
		confirmDialogOpen: false,
	});
	const [accountStatus, setAccountStatus] =
		useState<TMembershipAccountStatus | null>(null);
	const [accountStatusLoading, setAccountStatusLoading] = useState(true);

	const { loading, currentPlans, cancelSubscription } = usePremiumPackage();

	useEffect(() => {
		let active = true;

		const getMembershipAccountStatus = async () => {
			try {
				const response = await callApi({
					method: "GET",
					url: apis.SUBSCRIPTION_MEMBERSHIP,
				});
				if (active) {
					setAccountStatus(response?.accountStatus ?? null);
				}
			} catch {
				if (active) {
					setAccountStatus(null);
				}
			} finally {
				if (active) {
					setAccountStatusLoading(false);
				}
			}
		};

		getMembershipAccountStatus();

		return () => {
			active = false;
		};
	}, []);

	const managementCopy = accountStatus?.manageAt
		? manageSubscriptionCopy[accountStatus.manageAt]
		: null;

	const onClickCloseConfirmDialog = () => {
		setState((prevState) => ({
			...prevState,
			confirmDialogOpen: false,
			subscriptionId: null,
		}));
	};

	const onClickCancelSubscription = () => {
		const subscriptionId = state.subscriptionId;
		onClickCloseConfirmDialog();
		if (!subscriptionId) {
			return;
		}
		cancelSubscription(subscriptionId);
	};

	return (
		<div className="subscriptions-container">
			<Typography className="small-title-text bold">Subscriptions</Typography>
			{accountStatus && (
				<div className="item-separator-tiny">
					<Typography className="normal-text bold">
						{`Membership: ${accountStatus.premium ? "Active" : "Inactive"}`}
					</Typography>
					{managementCopy && (
						<Typography className="normal-text">{managementCopy}</Typography>
					)}
				</div>
			)}
			{!accountStatusLoading && !accountStatus && (
				<Typography className="normal-text item-separator-tiny">
					Membership status is temporarily unavailable.
				</Typography>
			)}
			<div className="current-plans-container item-separator-tiny">
				{currentPlans.map((currentPlan) => (
					<div
						className="current-plan-container"
						key={currentPlan.subscriptionId}>
						<Typography className="normal-text bold">
							{`${currentPlan.packageName} - ${currentPlan.currentPackage.name}`}
						</Typography>
						{currentPlan.canceled_at && (
							<Typography className="normal-text">
								{`Will expire on ${moment(currentPlan.duration.periodEnd * 1000).format("MMMM DD, YYYY")}`}
							</Typography>
						)}
						{!currentPlan.canceled_at && (
							<Typography className="normal-text">
								{`Next bill on ${moment(currentPlan.duration.periodEnd * 1000).format("MMMM DD, YYYY")}`}
							</Typography>
						)}
						<Typography className="normal-text">
							{`${currencyFormatter(currentPlan.currentPackage.currency).format(currentPlan.currentPackage.monthlyPrice)}/month`}
						</Typography>
						<div className="plan-button-container item-separator-tiny">
							<Link
								style={{ flex: 1 }}
								className="no-underline-link"
								to={`/memberships#${currentPlan.packageId}`}>
								<Typography className="view-plan-button normal-text">
									View plan
								</Typography>
							</Link>
							{!currentPlan.canceled_at && (
								<IconButton
									className="icon-button plan-menu-button"
									onClick={(e) => {
										setState((prevState) => ({
											...prevState,
											optionButtonAnchor: e.currentTarget,
											subscriptionId: currentPlan.subscriptionId,
										}));
									}}>
									<BsThreeDots />
								</IconButton>
							)}
						</div>
					</div>
				))}
			</div>
			{currentPlans.length === 0 && accountStatus?.premium !== true && (
				<Typography className="normal-text" textAlign="center">
					You have no active subscriptions
				</Typography>
			)}
			<Dialog
				open={state.confirmDialogOpen}
				className="confirm-dialog"
				onClose={onClickCloseConfirmDialog}>
				<DialogContent>
					<Typography className="normal-text">
						Are you sure you want to cancel subscription?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button className="dialog-button" onClick={onClickCloseConfirmDialog}>
						Oh, no thanks
					</Button>
					<Button className="dialog-button" onClick={onClickCancelSubscription}>
						Yes, I'm sure
					</Button>
				</DialogActions>
			</Dialog>
			<Menu
				open={Boolean(state.optionButtonAnchor)}
				anchorEl={state.optionButtonAnchor}
				className="option-menu"
				onClose={() => {
					setState((prevState) => ({
						...prevState,
						optionButtonAnchor: null,
						subscriptionId: null,
					}));
				}}>
				<MenuItem
					onClick={() => {
						setState((prevState) => ({
							...prevState,
							optionButtonAnchor: null,
							confirmDialogOpen: true,
						}));
					}}
					className="normal-text">
					Cancel subscription
				</MenuItem>
			</Menu>
			{(loading || accountStatusLoading) && <Loading />}
		</div>
	);
}
